import React, { useState, useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Account, AccountSnapshot, SnapshotFormData } from '../types';
import { accountsAPI } from '../services/api';
import { formatCurrency, formatDate, formatDateForInput, parseCurrencyInput, formatCurrencyInput } from '../utils/formatters';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { FormLabel } from './ui/form-label';
import { Calendar } from './ui/calendar';
import { cn } from '../lib/utils';
import InstitutionLogo from './InstitutionLogo';

interface UpdateValueModalProps {
  account: Account;
  /** When provided, modal is in edit mode for this snapshot. */
  snapshot?: AccountSnapshot;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

const EMPTY_FORM = (accountId: number): SnapshotFormData => ({
  account_id: accountId,
  date: new Date().toISOString().split('T')[0],
  value: 0,
  notes: '',
});

const UpdateValueModal: React.FC<UpdateValueModalProps> = ({
  account,
  snapshot,
  open,
  onClose,
  onSaved,
}) => {
  const isEdit = Boolean(snapshot);

  const [formData, setFormData] = useState<SnapshotFormData>(EMPTY_FORM(account.id));
  const [currencyInputValue, setCurrencyInputValue] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dateTriggerRef = useRef<HTMLButtonElement>(null);
  const dateCalendarRef = useRef<HTMLDivElement>(null);

  // Populate form from snapshot when editing, reset when adding
  useEffect(() => {
    if (!open) return;
    if (snapshot) {
      const date = formatDateForInput(snapshot.date);
      setFormData({ account_id: account.id, date, value: snapshot.value, notes: snapshot.notes || '' });
      setCurrencyInputValue(formatCurrencyInput(snapshot.value));
    } else {
      setFormData(EMPTY_FORM(account.id));
      setCurrencyInputValue('');
    }
    setError(null);
    setDatePickerOpen(false);
  }, [open, snapshot, account.id]);

  // Outside-click closes date picker
  useEffect(() => {
    if (!datePickerOpen) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!dateTriggerRef.current?.contains(t) && !dateCalendarRef.current?.contains(t)) {
        setDatePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [datePickerOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit && snapshot) {
        await accountsAPI.deleteSnapshot(snapshot.id);
      }
      await accountsAPI.addSnapshot(formData);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setDatePickerOpen(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-md w-full">
        <DialogHeader>
          {/* Account identity */}
          <div className="flex items-center gap-3 mb-1">
            <InstitutionLogo institution={account.institution} plaidLogoUrl={account.plaid_logo_url} />
            <div className="min-w-0">
              <DialogTitle className="text-base font-semibold leading-tight">
                {account.institution}
              </DialogTitle>
              <p className="text-sm text-muted-foreground mt-0.5 truncate">{account.name}</p>
            </div>
          </div>
        </DialogHeader>

        {/* Current value — anchor for the user */}
        <div className="flex items-baseline gap-3 py-3 border-y border-border">
          <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Current value
          </span>
          <span className="text-xl font-bold" style={{ color: 'var(--success-color)' }}>
            {formatCurrency(account.current_value || 0)}
          </span>
          {account.latest_snapshot_date && (
            <span className="text-xs text-muted-foreground ml-auto">
              as of {formatDate(account.latest_snapshot_date)}
            </span>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {/* Date */}
            <div className="space-y-2 relative">
              <FormLabel htmlFor="uvm-date-trigger">Date</FormLabel>
              <Button
                ref={dateTriggerRef}
                type="button"
                id="uvm-date-trigger"
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal bg-[var(--background-dark)] hover:bg-[var(--background-dark)] gap-2 border-input',
                  formData.date ? 'text-foreground hover:text-foreground' : 'text-muted-foreground/80 hover:text-muted-foreground/80'
                )}
                onClick={() => setDatePickerOpen(o => !o)}
              >
                <CalendarIcon className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                {formData.date ? format(parseISO(formData.date), 'MMM d, yyyy') : 'Pick a date'}
              </Button>
              {datePickerOpen && (
                <div
                  ref={dateCalendarRef}
                  className="balance-date-inline absolute left-0 top-full z-50 w-fit rounded-md border border-input bg-[var(--background-dark)] p-2"
                >
                  <Calendar
                    mode="single"
                    selected={formData.date ? parseISO(formData.date) : undefined}
                    onSelect={(selected) => {
                      if (selected) {
                        setFormData(prev => ({ ...prev, date: format(selected, 'yyyy-MM-dd') }));
                        setDatePickerOpen(false);
                        requestAnimationFrame(() => {
                          (document.getElementById('uvm-value') as HTMLInputElement | null)?.focus();
                        });
                      }
                    }}
                    defaultMonth={formData.date ? parseISO(formData.date) : new Date()}
                    captionLayout="dropdown"
                    startMonth={new Date(new Date().getFullYear() - 10, 0)}
                    endMonth={new Date(new Date().getFullYear() + 1, 11)}
                    fixedWeeks
                    hideNavigation
                    className="p-2 text-sm bg-[var(--background-dark)]"
                  />
                </div>
              )}
            </div>

            {/* Value */}
            <div className="space-y-2">
              <FormLabel htmlFor="uvm-value">New Value</FormLabel>
              <Input
                id="uvm-value"
                type="text"
                className="bg-[var(--background-dark)]"
                value={currencyInputValue}
                onChange={(e) => {
                  setCurrencyInputValue(e.target.value);
                  setFormData(prev => ({ ...prev, value: parseCurrencyInput(e.target.value) }));
                }}
                onBlur={(e) => {
                  const parsed = parseCurrencyInput(e.target.value);
                  setFormData(prev => ({ ...prev, value: parsed }));
                  setCurrencyInputValue(parsed ? formatCurrencyInput(parsed) : '');
                }}
                onPaste={(e) => {
                  e.preventDefault();
                  const text = e.clipboardData.getData('text/plain');
                  const parsed = parseCurrencyInput(text);
                  setFormData(prev => ({ ...prev, value: parsed }));
                  setCurrencyInputValue(parsed ? formatCurrencyInput(parsed) : '');
                }}
                placeholder="e.g. $1,234.56"
                required
                autoFocus
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <FormLabel>Notes <span className="text-muted-foreground/50 font-normal">(optional)</span></FormLabel>
            <Textarea
              className="resize-none bg-[var(--background-dark)]"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="e.g. After market correction, end of month statement…"
              rows={2}
            />
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : isEdit ? 'Update' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateValueModal;
